import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import DataTable from "react-data-table-component";
import AdminNavbar from "../components/AdminNavbar";
import { motion } from "framer-motion"; // 👈 Add framer-motion

function UserTransactions() {
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [error, setError] = useState("");
  const [totalAmount, setTotalAmount] = useState(0);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [searchPaymentType, setSearchPaymentType] = useState("");
  const [searchStoreName, setSearchStoreName] = useState("");
  const [filteredTotalAmount, setFilteredTotalAmount] = useState(0);

  const userName = location.state?.userName || "User";

  const columns = [
    {
      name: "Transaction Date",
      selector: (row) => new Date(row.transaction_date).toLocaleDateString(),
      sortable: true,
    },
    {
      name: "Store Name",
      selector: (row) => (
        <div>
          {row.storename}
          <br />
          <small>({row.storeid})</small>
        </div>
      ),
      sortable: true,
    },
    {
      name: "Payment Type",
      selector: (row) => row.payment_type,
      sortable: true,
    },
    {
      name: "Cheque Number",
      selector: (row) => (row.payment_type === "cheque" ? row.check_number : "-"),
      sortable: true,
    },
    {
      name: "Currency",
      selector: (row) => row.currency_type || "-",
      sortable: true,
    },
    {
      name: "Cash in Type",
      selector: (row) => row.cash_in_type || "-",
      sortable: true,
    },
    {
      name: "Amount",
      selector: (row) => row.amount,
      sortable: true,
    },
    {
      name: "File",
      selector: (row) =>
        row.file ? (
          <a href={row.file} target="_blank" rel="noopener noreferrer">
            View File
          </a>
        ) : (
          "-"
        ),
    },
  ];

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const response = await axios.get(`/admin/getUserTransactions/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const fetchedTransactions = response.data.transactions || [];
        setTransactions(fetchedTransactions);
        setFilteredTransactions(fetchedTransactions);

        const total = fetchedTransactions.reduce(
          (sum, transaction) => sum + (Number(transaction.amount) || 0),
          0
        );
        setTotalAmount(total);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch transactions.");
      }
    };

    fetchTransactions();
  }, [userId]);

  useEffect(() => {
    const filtered = transactions.filter(
      (transaction) =>
        transaction.payment_type.toLowerCase().includes(searchPaymentType.toLowerCase()) &&
        transaction.storename.toLowerCase().includes(searchStoreName.toLowerCase())
    );

    setFilteredTransactions(filtered);

    const filteredTotal = filtered.reduce(
      (sum, transaction) => sum + (Number(transaction.amount) || 0),
      0
    );
    setFilteredTotalAmount(filteredTotal);
  }, [searchPaymentType, searchStoreName, transactions]);

  const exportToExcel = (data, filename, totalAmount) => {
    const formattedData = data.map(({ id, file, ...rest }) => ({
      "Transaction Date": new Date(rest.transaction_date).toLocaleDateString(),
      "Store Name": rest.storename,
      "Store ID": rest.storeid,
      "Currency": rest.currency_type || "-",
      "Cash in Type": rest.cash_in_type || "-",
      "Payment Type": rest.payment_type || "-",
      "Cheque Number": rest.payment_type === "cheque" ? rest.check_number : "-",
      "Amount": Number(rest.amount) || 0,
    }));

    formattedData.push({
      "Transaction Date": "",
      "Store Name": "",
      "Store ID": "",
      "Currency": "",
      "Cash in Type": "",
      "Payment Type": "Total Amount",
      "Cheque Number": "",
      "Amount": totalAmount.toFixed(2),
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    worksheet["!cols"] = [
      { wch: 15 },
      { wch: 35 },
      { wch: 10 },
      { wch: 10 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 12 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");
    XLSX.writeFile(workbook, `${filename}.xlsx`);
  };

  return (
    <div>
      <AdminNavbar />

      <motion.div
        className="container mt-5"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <motion.h1
          className="text-center mb-4"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          {`${userName}'s Transactions`}
        </motion.h1>

        <motion.div
          className="d-flex justify-content-between mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>
            Back
          </button>
          <div>
            <button
              className="btn btn-success me-2"
              onClick={() => exportToExcel(transactions, "All_Transactions", totalAmount)}
            >
              Export All to Excel
            </button>
            <button
              className="btn btn-warning"
              onClick={() =>
                exportToExcel(filteredTransactions, "Filtered_Transactions", filteredTotalAmount)
              }
              disabled={filteredTransactions.length === 0}
            >
              Export Filtered to Excel
            </button>
          </div>
        </motion.div>

        {error && <motion.div className="alert alert-danger">{error}</motion.div>}

        <motion.div
          className="row mb-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <div className="col-md-6">
            <input
              type="text"
              className="form-control"
              placeholder="Search by Payment Type"
              value={searchPaymentType}
              onChange={(e) => setSearchPaymentType(e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <input
              type="text"
              className="form-control"
              placeholder="Search by Store Name"
              value={searchStoreName}
              onChange={(e) => setSearchStoreName(e.target.value)}
            />
          </div>
        </motion.div>

        <motion.div
          className="table-responsive"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <DataTable
            columns={columns}
            data={filteredTransactions}
            pagination
            highlightOnHover
            responsive
          />

          {transactions.length > 0 && (
            <motion.div
              className="d-flex justify-content-end mt-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <div className="fw-bold me-4">Total: {totalAmount.toFixed(2)}</div>
              <div className="fw-bold">Filtered Total: {filteredTotalAmount.toFixed(2)}</div>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </div>
  );
}

export default UserTransactions;
